import React, { useState, useEffect } from "react";
import { SparklesIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { useGeminiMutations } from "../../../../../hooks/data/useProjectMutations";
import type { Project } from "@/features/Projects";

interface TechnicalDescriptionEditorProps {
  project: Project;
  initialDescription?: string;
  onSave: (description: string) => Promise<void>;
  onNext: () => void;
}

export default function TechnicalDescriptionEditor({
  project,
  initialDescription = "",
  onSave,
  onNext,
}: TechnicalDescriptionEditorProps) {
  const [technicalDescription, setTechnicalDescription] = useState(initialDescription);
  const [isEditing, setIsEditing] = useState(!initialDescription);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const { expandTechnicalDescription, isExpandingTechnicalDescription } = useGeminiMutations();

  // Detect changes
  useEffect(() => {
    setHasUnsavedChanges(technicalDescription !== initialDescription);
  }, [technicalDescription, initialDescription]);

  const handleExpandWithAI = async () => {
    if (!technicalDescription.trim()) {
      return;
    }

    try {
      const response = await expandTechnicalDescription({
        initialTechnicalDescription: technicalDescription,
        projectDescription: project.description,
        objectives: project.objectives || [],
      });
      
      setTechnicalDescription(response.expandedTechnicalDescription);
    } catch (error) {
      console.error("Erro ao expandir descrição técnica:", error);
    }
  };

  const handleSave = async () => {
    if (!technicalDescription.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(technicalDescription);
      setHasUnsavedChanges(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Erro ao salvar descrição técnica:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (hasUnsavedChanges) {
      await handleSave();
    }
    onNext();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setTechnicalDescription(initialDescription);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const hasContent = technicalDescription.trim().length > 0;
  const isExpandable = hasContent && technicalDescription.length < 1000; // Allow expansion if content is short

  if (!isEditing && hasContent) {
    // Display mode - show the technical description with edit option
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Descrição Técnica do Projeto
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Definição técnica detalhada da solução proposta
            </p>
          </div>
          <Button
            onClick={handleEdit}
            variant="secondary"
            size="sm"
            leftIcon={<PencilIcon className="h-4 w-4" />}
          >
            Editar
          </Button>
        </div>

        {/* Technical Description Display */}
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {technicalDescription}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isExpandable && (
              <Button
                onClick={handleExpandWithAI}
                disabled={isExpandingTechnicalDescription}
                variant="secondary"
                size="sm"
                leftIcon={<SparklesIcon className="h-4 w-4" />}
              >
                {isExpandingTechnicalDescription ? "Expandindo..." : "Expandir com IA"}
              </Button>
            )}
          </div>
          
          <Button onClick={handleNext} size="sm">
            Continuar para Detalhes Técnicos
          </Button>
        </div>
      </div>
    );
  }

  // Edit/Create mode
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">
          {hasContent ? "Editar Descrição Técnica" : "Criar Descrição Técnica"}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {hasContent 
            ? "Edite a descrição técnica ou use a IA para expandir o conteúdo"
            : "Descreva a solução técnica do projeto ou comece com ideias básicas e use a IA para expandir"
          }
        </p>
      </div>

      {/* Technical Description Input */}
      <div>
        <label htmlFor="technical-description" className="block text-sm font-medium text-gray-700 mb-2">
          Descrição Técnica
        </label>
        <textarea
          id="technical-description"
          value={technicalDescription}
          onChange={(e) => setTechnicalDescription(e.target.value)}
          rows={12}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Descreva a solução técnica do projeto...

Por exemplo:
- Arquitetura do sistema (monolito, microserviços, etc.)
- Tecnologias principais (linguagens, frameworks)
- Banco de dados e armazenamento
- Infraestrutura e deployment
- Considerações de segurança e performance
- Integrações com sistemas externos"
        />
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          <span>Mínimo 10 caracteres</span>
          <span>{technicalDescription.length}/2000 caracteres</span>
        </div>
      </div>

      {/* AI Expand Button */}
      {hasContent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <SparklesIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900">
                Expandir com Inteligência Artificial
              </h4>
              <p className="mt-1 text-sm text-blue-700">
                Use a IA para enriquecer sua descrição técnica com detalhes sobre arquitetura, 
                tecnologias recomendadas e considerações importantes.
              </p>
              <Button
                onClick={handleExpandWithAI}
                disabled={isExpandingTechnicalDescription || technicalDescription.trim().length < 10}
                variant="primary"
                size="sm"
                className="mt-3"
              >
                {isExpandingTechnicalDescription ? "Expandindo..." : "Expandir Descrição Técnica"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600">
              ● Alterações não salvas
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          {hasContent && (
            <Button
              onClick={handleCancel}
              disabled={isSaving}
              variant="secondary"
              size="sm"
            >
              Cancelar
            </Button>
          )}
          
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasContent || technicalDescription.trim().length < 10}
            size="sm"
          >
            {isSaving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}